import { eventWords } from '../../lib/sentences/words'
import { formatOfficerDate, formatOfficerValue } from '../../lib/officerFormat'
import type { ViewScope } from './context'

export type HistoryCategory =
  'record' | 'documents' | 'evidence' | 'merge' | 'look_through' | 'sign_off'
export interface HistorySource {
  kind: 'record_event' | 'evidence_decision' | 'record_edit' | 'close_check' | 'document_receipt'
  id: string
  phase: string
}
export interface HistoryEntry {
  source: HistorySource
  occurred_at: string
  captured_at: string
  financial_date: string | null
  category: HistoryCategory
  attribution_basis: string
  content: {
    action: string
    outcome: string
    subject: { type: string; id: string; label: string | null }
    actor: {
      id: string | null
      name: string | null
      role: string | null
      attribution: 'retained' | 'unavailable'
    }
    reason: string | null
    dates: Record<string, string>
    changes: {
      field: string
      label: string
      before: unknown
      after: unknown
      value_status: 'proposed' | 'recorded'
    }[]
    checks: {
      id: string
      label: string | null
      result: string
      required: boolean
      reason: string | null
    }[]
    documents: {
      document_id: string
      title: string | null
      page: number | null
      available: boolean
    }[]
    related: HistorySource[]
    preview?: {
      complete: false
      omitted_counts: Record<string, number>
      shortened_fields: string[]
    } | null
  }
}
export interface HistoryPage {
  items: HistoryEntry[]
  next_cursor: string | null
  snapshot: string
  coverage: { status: 'retained_only'; message: string; producer_capture_active: boolean }
}
export const HISTORY_CATEGORIES: { value: HistoryCategory | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'record', label: 'Record' },
  { value: 'documents', label: 'Documents' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'merge', label: 'Merge' },
  { value: 'look_through', label: 'Look-through' },
  { value: 'sign_off', label: 'Sign-off' },
]
export function historyIdentity(source: HistorySource): string {
  return `${source.kind}:${source.id}:${source.phase}`
}
export function historyHref(scope: ViewScope, source?: HistorySource): string {
  const params = new URLSearchParams()
  if (scope.client_id) params.set('client', scope.client_id)
  if (source) {
    params.set('entry_kind', source.kind)
    params.set('entry_id', source.id)
    params.set('entry_phase', source.phase)
  }
  return `/v2/funds/${encodeURIComponent(scope.fund_id!)}/history${params.size ? '?' + params : ''}`
}
export function historyDetailUrl(fund: string, source: HistorySource): string {
  return `/api/funds/${encodeURIComponent(fund)}/history/${encodeURIComponent(source.kind)}/${encodeURIComponent(source.id)}/${encodeURIComponent(source.phase)}`
}
export function historyTitle(entry: HistoryEntry): string {
  const action = entry.content.action
  if (action === 'entity.updated' && entry.content.outcome === 'wording_updated')
    return 'Field wording clarified'
  if (action === 'entity.corroborated' && entry.content.outcome === 'wording_retained')
    return 'Existing field wording retained'
  if (action === 'evidence.accept' || action === 'evidence.automatic_accept')
    return entry.content.outcome === 'submitted'
      ? 'Evidence acceptance submitted'
      : 'Evidence accepted'
  if (action === 'evidence.discard')
    return entry.content.outcome === 'submitted'
      ? 'Evidence discard submitted'
      : 'Evidence discarded'
  const names: Record<string, string> = {
    'removal.requested': 'Record removal requested',
    'removal.approved': 'Record removal approved',
    'removal.rejected': 'Record removal rejected',
    'removal.cancelled': 'Record removal request cancelled',
    'record_edit.correction': 'Record correction submitted',
    'record_edit.amendment': 'Record amendment submitted',
    'record_edit.undo': 'Restore submitted',
    'record_edit.withdrawal': 'Edit withdrawal submitted',
    'record_edit.approval_decided': 'Record change approved',
    'record_edit.rejected': 'Record change rejected',
    'record_edit.withdrawn': 'Record edit withdrawn',
    'record_edit.superseded': 'Record edit replaced',
    'subchain.checked': 'Close check completed',
    'subchain.closed': 'Subchain closed',
    'subchain.close_rechecked': 'Close checked again',
    'subchain.reopened': 'Subchain Close reopened',
    'fund.reporting_signed_off': 'Fund signed off',
    'fund.reporting_revalidated': 'Fund sign-off checked again',
    'document.received': 'Document received',
    'identity.merged': 'Records merged',
    'identity.kept_separate': 'Records kept separate',
    lookthrough_terminal: 'Look-through stopped',
    lookthrough_continues: 'Look-through confirmed',
    lookthrough_pending_review: 'Look-through deferred',
    'evidence_status.waived': 'Evidence requirement waived',
    'evidence_status.decision_refused': 'Evidence decision refused',
    'evidence_status.decision_stale': 'Evidence decision needs review',
  }
  const value = names[action] ?? eventWords(action) ?? 'Recorded event'
  return value.charAt(0).toUpperCase() + value.slice(1)
}
export function historyActor(entry: HistoryEntry): string {
  return (
    entry.content.actor.name ||
    (entry.content.actor.id ? 'Name not retained' : 'Actor not recorded')
  )
}
export function historyValue(value: unknown): string {
  if (value == null) return 'Not recorded'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) return value.map(historyValue).join(', ') || 'Not recorded'
  if (typeof value === 'object')
    return 'value' in value
      ? historyValue((value as { value: unknown }).value)
      : 'Value not available'
  const text = String(value)
  if (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(text)) return 'Name not retained'
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return formatOfficerDate(text) ?? 'Not recorded'
  return formatOfficerValue(value)
}

export function historyOutcome(entry: HistoryEntry): string {
  const labels: Record<string, string> = {
    submitted: 'Submitted',
    applied: 'Completed',
    eligible: 'Eligible for Close',
    not_eligible: 'Not eligible for Close',
    superseded: 'Superseded',
    passed: 'Passed',
    failed: 'Failed',
    revalidated: 'Checked again',
    signed: 'Signed off',
    recorded: 'Recorded',
    received: 'Received',
    deleted: 'Deleted',
    terminal: 'Look-through stopped',
    walk_through: 'Look-through confirmed',
    pending_review: 'Pending review',
    pending: 'Pending',
    stale: 'Needs a fresh review',
    refused: 'Refused',
  }
  return labels[entry.content.outcome] ?? formatOfficerValue(entry.content.outcome)
}
